import type { TFunction } from 'i18next';
import { z } from 'zod';

export const DONOR_FIELD_MAX_LENGTHS = {
  donorName: 30,
  pedName: 50,
  metalTag: 15,
  lastActivity: 2,
  tattoo: 10,
  regPrefix: 4,
  registrationNumber: 20,
} as const;

export type DonorLimitedField = keyof typeof DONOR_FIELD_MAX_LENGTHS;

export const getDonorNameMaxLength = (tattoo?: string | null) => {
  const earTag = tattoo?.trim() ?? '';
  if (!earTag) return DONOR_FIELD_MAX_LENGTHS.donorName;

  // tog_name holds `${donorName} (${tattoo})` in nvarchar(43)
  const compositeMaxLength = 43;
  const wrapperLength = 3;
  const earTagLength = Math.min(earTag.length, DONOR_FIELD_MAX_LENGTHS.tattoo);
  const budget = compositeMaxLength - wrapperLength - earTagLength;

  return Math.min(DONOR_FIELD_MAX_LENGTHS.donorName, budget);
};

const DONOR_FIELD_LABEL_KEYS: Record<DonorLimitedField, string> = {
  donorName: 'donor.donorName',
  pedName: 'donor.pedigreeName',
  metalTag: 'donor.metalTag',
  lastActivity: 'donor.lastActivity',
  tattoo: 'donor.earTag',
  regPrefix: 'fields.country',
  registrationNumber: 'fields.registrationNumber',
};

export const getMaxLengthMessage = (
  t: TFunction,
  field: DonorLimitedField,
  max: number,
) =>
  String(t('donor.characterLimitExceeded'))
    .replace('{label}', String(t(DONOR_FIELD_LABEL_KEYS[field])))
    .replace('{max}', String(max));

export const createDonorFormSchema = (
  t: TFunction,
  enforceCharacterLimits = false,
) => {
  const limited = (field: DonorLimitedField) => {
    const max = DONOR_FIELD_MAX_LENGTHS[field];
    return enforceCharacterLimits
      ? z.string().max(max, getMaxLengthMessage(t, field, max))
      : z.string();
  };

  return z
    .object({
      donorName: z.string().nullish(),
      breedId: z.string().min(1, t('donor.breedIsRequired')),
      pedName: limited('pedName').optional(),
      metalTag: limited('metalTag').nullish(),
      lastActivity: limited('lastActivity').nullish(),
      tattoo: limited('tattoo').optional(),
      regPrefix: (enforceCharacterLimits
        ? limited('regPrefix')
        : z
            .string()
            .max(
              DONOR_FIELD_MAX_LENGTHS.regPrefix,
              t('donor.countryCodeTooLong'),
            )
      )
        .optional()
        .nullable(),
      birth: z.date().optional().nullable(),
      ownerId: z.string().min(1, t('donor.ranchIsRequired')),
      yardId: z.string().min(1, t('donor.yardIsRequired')),
      registrationNumber: limited('registrationNumber').optional(),
      received: z.date().nullish(),
      pregnant: z.boolean().default(false),
      ivfStatusId: z.string().nullish(),
      exportDonor: z.boolean().default(false),
      housingId: z.string().nullish(),
    })
    .refine(
      (data) => {
        const donorName = data.donorName || '';
        const tattoo = data.tattoo || '';
        return donorName.length > 0 || tattoo.length > 0;
      },
      {
        message: t('donor.nameOrEarTagRequired'),
        path: ['donorName'],
      },
    )
    .refine(
      (data) =>
        !enforceCharacterLimits ||
        (data.donorName?.length ?? 0) <= getDonorNameMaxLength(data.tattoo),
      (data) => ({
        message: getMaxLengthMessage(
          t,
          'donorName',
          getDonorNameMaxLength(data.tattoo),
        ),
        path: ['donorName'],
      }),
    );
};

export type DonorFormValues = z.infer<ReturnType<typeof createDonorFormSchema>>;
